import { isindex } from "~/utils/iterate-object/array";
import { isown, symbolsof, valueof } from "~/utils/iterate-object/object";

import { type ExpandedFieldStateManager } from "./ExpandedFieldStateManager";
import { typeInfoFor } from "./TypeTagInfo";
import { type EmptyArraySlotValueRow, type ValueInspectorRowInfo } from "./ValueInspectorRowInfo";
import { type ValuePath, concatPath } from "./ValuePath";
import { traverseValue } from "./traverseValue";

export function traverseArrayValue(
	fieldExpander: ExpandedFieldStateManager,
	pathToO: ValuePath,
	a: Array<never>,
	shallow: boolean
) {
	const fields: ValueInspectorRowInfo[] = [];

	// Index of the last non-empty slot in the array. If this is behind `i1` we know has 1 or more
	// empty slots.
	let i0 = -1;

	// The index we use to iterate through the array.
	let i1 = 0;
	for (const n = a.length; i1 < n; ++i1) {
		if (!(i1 in a)) {
			continue;
		}

		//
		// Emit `empty` if there were 1 or more empty slots between values in the array.
		//

		if (i1 > i0 + 1) {
			fields.push(emptyArraySlotValueRow(pathToO, i0, i1));
		}

		//
		// Emit array element itself.
		//

		const value = valueof(a, i1);
		const typeInfo = typeInfoFor(value);
		const newPath = concatPath(pathToO, {
			type: "ArrayElement",
			key: i1,
			value,
			typeInfo,
		});
		i0 = i1;

		fields.push(...traverseValue(fieldExpander, newPath, typeInfo, shallow));
	}

	//
	// Print any empty slots left over at the end.
	//

	if (i1 > i0 + 1) {
		fields.push(emptyArraySlotValueRow(pathToO, i0, i1));
	}

	//
	// Print any non-index keys, e.g., if there's a `3.5` key in an array, it would be reported as:
	// `[1, 2, 3.5: 3]`.
	//

	for (const key in a) {
		if (!isindex(key) && isown(a, key)) {
			const value = valueof(a, key);
			const typeInfo = typeInfoFor(value);
			const newPath = concatPath(pathToO, {
				type: "ArrayElement",
				key,
				value,
				typeInfo,
			});

			fields.push(...traverseValue(fieldExpander, newPath, typeInfo, shallow));
		}
	}

	//
	// Print any symbols.
	//

	for (const symbol of symbolsof(a)) {
		const value = valueof(a, symbol);
		const typeInfo = typeInfoFor(value);
		const newPath = concatPath(pathToO, {
			type: "ArrayElement",
			key: symbol,
			value,
			typeInfo,
		});

		fields.push(...traverseValue(fieldExpander, newPath, typeInfo, shallow));
	}

	return fields;
}

function emptyArraySlotValueRow(
	pathToO: ValuePath,
	i0: number,
	i1: number
): EmptyArraySlotValueRow {
	return {
		type: "EmptyArraySlotValueRow",
		path: concatPath(pathToO, {
			type: "ArrayElement",
			key: i0 + 1,
			value: undefined,
			typeInfo: typeInfoFor(undefined),
		}),
		emptySlots: i1 - i0 - 1,
	};
}
